/**
 * Client-side entitlement evaluation for the app catalogue.
 *
 * Mirrors the backend `EntitlementEvaluator` (Appendix B / Appendix E) exactly
 * so the catalogue admin "Entitlement preview" smoke test and the user-facing
 * Apps page agree with what the server would decide.
 *
 * Predicate semantics:
 *  - Empty entitlement                  => visible to every authenticated user.
 *  - anyOfRoles present                 => caller must hold at least one of them.
 *  - anyOfRights present                => caller must hold at least one of them.
 *  - allOfRights present                => caller must hold every one of them.
 *  - When several lists are present they are combined with AND.
 *  - All comparisons are case-insensitive.
 *  - A `*` wildcard right satisfies any right requirement.
 */

import type { AppEntitlement, AppManifest } from '@actschurch/ccms-sdk';

/** Result of an entitlement check, with a human-readable explanation. */
export interface EntitlementResult {
  /** Whether the caller is entitled to see the app. */
  entitled: boolean;
  /** Per-clause breakdown for the admin preview. */
  reasons: EntitlementReason[];
}

/** A single predicate clause and whether the sample user satisfied it. */
export interface EntitlementReason {
  /** The clause label, e.g. "Any of roles". */
  clause: string;
  /** The values the clause requires. */
  required: string[];
  /** Whether the sample user satisfied this clause. */
  satisfied: boolean;
}

const norm = (v: string) => v.trim().toLowerCase();

/**
 * Whether the entitlement predicate has no clauses (visible to everyone).
 */
export function isEntitlementEmpty(entitlement?: AppEntitlement | null): boolean {
  if (!entitlement) return true;
  const { anyOfRoles, anyOfRights, allOfRights } = entitlement;
  return (
    (!anyOfRoles || anyOfRoles.length === 0) &&
    (!anyOfRights || anyOfRights.length === 0) &&
    (!allOfRights || allOfRights.length === 0)
  );
}

/**
 * Evaluate an entitlement predicate against a sample user's roles and rights.
 *
 * @param entitlement - The manifest's entitlement predicate.
 * @param callerRoles - The sample user's roles.
 * @param callerRights - The sample user's rights / permissions.
 */
export function evaluateEntitlement(
  entitlement: AppEntitlement | null | undefined,
  callerRoles: string[],
  callerRights: string[]
): EntitlementResult {
  const reasons: EntitlementReason[] = [];

  if (isEntitlementEmpty(entitlement)) {
    return {
      entitled: true,
      reasons: [
        { clause: 'No predicate', required: [], satisfied: true },
      ],
    };
  }

  const roles = new Set((callerRoles ?? []).map(norm));
  const rights = new Set((callerRights ?? []).map(norm));
  const hasWildcard = rights.has('*');

  let entitled = true;

  const anyOfRoles = entitlement?.anyOfRoles ?? [];
  if (anyOfRoles.length > 0) {
    const satisfied = anyOfRoles.some((r) => roles.has(norm(r)));
    reasons.push({ clause: 'Any of roles', required: anyOfRoles, satisfied });
    if (!satisfied) entitled = false;
  }

  const anyOfRights = entitlement?.anyOfRights ?? [];
  if (anyOfRights.length > 0) {
    const satisfied = hasWildcard || anyOfRights.some((r) => rights.has(norm(r)));
    reasons.push({ clause: 'Any of rights', required: anyOfRights, satisfied });
    if (!satisfied) entitled = false;
  }

  const allOfRights = entitlement?.allOfRights ?? [];
  if (allOfRights.length > 0) {
    const satisfied = hasWildcard || allOfRights.every((r) => rights.has(norm(r)));
    reasons.push({ clause: 'All of rights', required: allOfRights, satisfied });
    if (!satisfied) entitled = false;
  }

  return { entitled, reasons };
}

/**
 * Convenience predicate: would the given user see this manifest?
 * (Combines the entitlement check with the `enabled` flag.)
 */
export function isAppVisibleTo(
  manifest: AppManifest,
  callerRoles: string[],
  callerRights: string[]
): boolean {
  if (!manifest.enabled) return false;
  return evaluateEntitlement(manifest.entitlement, callerRoles, callerRights).entitled;
}
